import { isValidFloatNumber } from '@/helpers/isValidFloatNumber';
import { getSizeUnitItemsByNature } from '@/modules/analysis/helpers/getUnitItemsByNature';
import { Sample } from '@/modules/analysis/models/analysisRequest';
import { NatureEnum } from '@/modules/analysis/models/natureEnum';
import { ErrorEnum } from '@/models/errorEnum';
import {
  //eslint-disable-next-line
  // @ts-ignore
  FormSample,
  //eslint-disable-next-line
  // @ts-ignore
  MAX_SAMPLES_SIZE
} from '@/modules/analysis/components/create-request/BatchIdentificationCarousel.vue';

//eslint-disable-next-line
const REGEX_DATETIME_EN = /^\d{4}[\/-][01]\d[\/-][0-3]\d$/;
//eslint-disable-next-line
const REGEX_DATETIME_FR = /^[0-3]\d[\/-][01]\d[\/-]\d{4}$/;
export const NEW_SAMPLES_CSV_VERSION = '1';

export async function extractFormSampleFromCsv(file: File): Promise<FormSample> {
  const formSample: FormSample = {
    nature: '',
    count: 0,
    samples: []
  };

  await file.text().then((text) => {
    const lines = text.split('\n');
    verifyCsvVersion(lines);
    formSample.nature = extractNature(lines);
    formSample.samples = extractSamples(lines, formSample.nature);
    formSample.count = formSample.samples.length;
  });

  return formSample;
}

export function extractNature(lines: string[]): string {
  let nature = '';
  for (const line of lines) {
    if (line.startsWith('nature')) {
      nature = line.split(';')[1];
      verifyNature(nature);
      return nature;
    }
  }
  throw Error(ErrorEnum.MISSING_NATURE_ATTRIBUTE);
}

function extractSamples(lines: string[], nature: string): Sample[] {
  const samples: Sample[] = [];
  let attributes: string[] = [];
  let cptSamples = 0;
  for (let line of lines) {
    if (line.match(/\*/g)) {
      continue;
    }
    line = line.replace(/\n|\r|(\n\r)|:/g, '').trim();
    if (line.startsWith('reference')) {
      attributes = line.split(';');
      verifySamplesAttributes(attributes);
    } else if (attributes.length > 0 && line.length > 0 && !line.startsWith(';')) {
      if (cptSamples >= parseInt(MAX_SAMPLES_SIZE)) {
        throw Error(ErrorEnum.INVALID_SAMPLES_SIZE);
      }
      // samples attributes are correct, they can be created
      samples.push(convertLineToSample(line, attributes.length, nature));
      cptSamples++;
    }
  }

  if (attributes.length === 0) {
    throw Error(ErrorEnum.INVALID_SAMPLES_ATTRIBUTES);
  }

  if (cptSamples === 0) {
    throw Error(ErrorEnum.MISSING_SAMPLES);
  }

  return samples;
}

export function convertLineToSample(line: string, attributesLength: number, nature: string): Sample {
  const sampleValues = line.split(';');

  if (sampleValues.length !== attributesLength) {
    throw Error(ErrorEnum.INVALID_SAMPLE_VALUES_NUMBER);
  }

  // verify size value
  const sizeInput = sampleValues[2];
  if (!isValidFloatNumber(sizeInput)) {
    throw Error(ErrorEnum.INVALID_SIZE_VALUE);
  }

  // verify sizeUnitValue
  const sizeUnitInput = sampleValues[3];
  if (!isValidSizeUnit(sizeUnitInput, nature)) {
    throw Error(ErrorEnum.INVALID_SIZE_UNIT_VALUE);
  }

  // verify samplingDate
  let samplingDateInput = sampleValues[4];
  if (!isValidSamplingDate(samplingDateInput)) {
    throw Error(ErrorEnum.INVALID_SAMPLING_DATE_VALUE);
  }
  samplingDateInput = normalizeDate(samplingDateInput);

  const sample = {
    reference: sampleValues[0],
    origin: sampleValues[1],
    size: sizeInput,
    sizeUnit: sizeUnitInput,
    samplingDate: samplingDateInput
  } as Sample;

  return sample;
}

export function verifyNature(nature: string): void {
  for (const item of NatureEnum) {
    if (nature === item.value) {
      return;
    }
  }
  throw Error(ErrorEnum.INVALID_NATURE_ATTRIBUTE);
}

export function verifySamplesAttributes(attributes: string[]): void {
  const sample = {
    reference: '',
    origin: '',
    size: '',
    sizeUnit: '',
    samplingDate: ''
  } as Sample;
  const sampleKeys = Object.keys(sample);

  if (!(JSON.stringify(attributes) === JSON.stringify(sampleKeys))) {
    throw Error(ErrorEnum.INVALID_SAMPLES_ATTRIBUTES);
  }
}

export function isValidSizeUnit(sizeUnitInput: string, nature: string): boolean {
  return getSizeUnitItemsByNature(nature).includes(sizeUnitInput);
}

export function isValidSamplingDate(samplingDate: string): boolean {
  // format: 2021-01-18 or 2021/01/18 or 18-01-2021 or 18/01/2021
  if (samplingDate.match(REGEX_DATETIME_EN) || samplingDate.match(REGEX_DATETIME_FR)) {
    return true;
  }
  return false;
}

export function normalizeDate(samplingDate: string): string {
  if (samplingDate.match(REGEX_DATETIME_FR)) {
    let dateArr = [];
    if (samplingDate.includes('-')) {
      dateArr = samplingDate.split('-');
    } else {
      dateArr = samplingDate.split('/');
    }
    const dateFormatted = dateArr[2] + '-' + dateArr[1] + '-' + dateArr[0];
    return dateFormatted;
  }
  return samplingDate;
}

export function verifyCsvVersion(lines: string[]): void {
  for (let line of lines) {
    line = line.replace(/\n|\r|(\n\r)/g, ' ').trim();
    if (line.startsWith('version')) {
      const versionArr = line.split(';');
      if (versionArr.length < 2 || !(NEW_SAMPLES_CSV_VERSION === versionArr[1])) {
        throw Error(ErrorEnum.INVALID_VERSION_VALUE);
      }
      return;
    }
  }
  throw Error(ErrorEnum.MISSING_VERSION_ATTRIBUTE);
}
